import { Component, input, output, DestroyRef, WritableSignal, signal, Signal, computed, OnInit } from '@angular/core';
import _ from 'lodash';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';
import { OptionGroup, ViolationRulesFiltersModel } from './violation-rules-filters.model';
import { CommonModule } from '@angular/common';
import { AccordionComponent } from '../../accordion/accordion.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export enum ViolationRulesFilter {
  GROUP_NAME = 'GROUP_NAME',
  TAGS = 'TAGS',
  STATUS = 'STATUS',
  SEVERITY = 'SEVERITY',
  AI_SERVICE = 'AI_SERVICE',
  LIGHTNING_SUPPORT = 'LIGHTNING_SUPPORT',
}

@Component({
  selector: 'app-violation-rules-filters',
  standalone: true,
  templateUrl: './violation-rules-vertical-filters.component.html',
  styleUrls: ['./violation-rules-vertical-filters.component.scss'],
  imports: [CommonModule, AccordionComponent, MatCheckboxModule, BeautifyDisplayStringPipe],
})
export class ViolationRulesVerticalFiltersComponent implements OnInit {
  public pageFilters = input.required<ViolationRulesFiltersModel>();
  public isFiltersMenuOpen = input.required<boolean>();

  public isFiltersMenuOpenChange = output<boolean>();
  public optionOnSelection = output<ISelectOption[]>();

  public options: Signal<OptionGroup[]>;
  public expandedGroups: WritableSignal<Set<string>> = signal(new Set());
  public selectedIds: Set<string> = new Set();

  protected ViolationRulesFilterName = ViolationRulesFilter;

  constructor(private readonly _destroyRef: DestroyRef) {
    this.options = computed(() => this.pageFilters().optionsGroups());
  }

  ngOnInit(): void {
    // Subscribe to filter updates to sync selectedIds with actual selected filters
    this.pageFilters()
      .onUpdate$.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        const selectedOptions = this.pageFilters().getAllSelectedOptions();
        this.selectedIds = new Set(selectedOptions.map((opt) => opt.id));
      });

    // Initialize selectedIds with current state
    const selectedOptions = this.pageFilters().getAllSelectedOptions();
    this.selectedIds = new Set(selectedOptions.map((opt) => opt.id));
  }

  public expandClicked(option: ISelectOptionGroups, status: boolean) {
    this.expandedGroups.update((expandedSet) => {
      status ? expandedSet.add(option.displayName ?? '') : expandedSet.delete(option.displayName ?? '');
      return expandedSet;
    });
  }

  public onToggleClick() {
    this.isFiltersMenuOpenChange.emit(!this.isFiltersMenuOpen());
  }

  public onSelect(id: string, filter: ViolationRulesFilter): void {
    if (!this.selectedIds.has(id)) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
    const selectedOptions = this._getSelectedOptions();
    this.pageFilters().updateFilterFromUserSelection(filter, selectedOptions);
    this.optionOnSelection.emit(selectedOptions);
  }

  private _getSelectedOptions(): ISelectOption[] {
    const selectedIndexArray = Array.from(this.selectedIds);
    const optionsById = _.keyBy(this.pageFilters().getAllFiltersOptions().value, 'id');

    /// This is needed because there are options that can be selected but are not shown.
    const selectedOptions = selectedIndexArray.map((id: string) => optionsById[id]);

    return selectedOptions.filter((options) => typeof options !== 'undefined');
  }
}
